import {mergeMap, take} from 'rxjs/operators';
import {Component, ElementRef, ViewChild} from '@angular/core';
import {AuthService} from './../shared/services/auth.service';
import {UserService} from './../shared/services/user.service';
import {ProjectId, UserId} from './../shared/types/ids';
import {FeatureService} from './../shared/services/feature.service';
import {NavigationEnd, Router} from '@angular/router';
import {isDefined, isUUID} from './../shared/utils/utils';

import {TranslateService} from '@ngx-translate/core';
import {WebmateOptions} from '@webmate-web-core/webmate-options';
import {TestService} from '@webmate-web-core/shared/services/test.service';
import {User} from '@webmate-web-core/shared/types/user/user';
import {RunTestDialogComponent, RunTestDialogToolConfig} from '@webmate-web-core/shared/tools/run-test-dialog/run-test-dialog.component';
import {ModalToolContainerService} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.service';
import {CreateOrCancelTestRunPermission} from '@webmate-web-core/shared/constants/permission.constants';
import {Feature} from "@webmate-web-core/shared/types/feature";
import {SelectTestComponent, SelectTestToolConfig} from '@webmate-web-core/shared/tools/select-test/select-test.component';
import {WorkbenchOpenService} from '@webmate-web-core/shared/workbench-ipc/workbench-open.service';
import {faTv} from '@fortawesome/pro-light-svg-icons';
import {MarketplaceComponent, MarketplaceToolConfig} from '@webmate-web-core/marketplace/marketplace.component';
import {MarketplaceFeatureId} from '@webmate-web-core/shared/types/marketplace';

@Component({
    selector: 'webmate',
    templateUrl: './webmate.component.html',
    styleUrls: ['./webmate.component.less'],
})
export class WebmateComponent {

    demoInstance: boolean;

    userId?: UserId;

    currentUser?: User;

    username: string = '';
    isAuthenticated: boolean = false;
    showAdmin: boolean = false;
    isWebmateAdmin = false;
    viewId: string = '';
    adminWarning?: string;
    internalSupportFeature: Feature = { "isActive": false };
    marketplaceAvailable = false;

    currentProject?: ProjectId;
    sidebarRightOpen: boolean = false;

    displayConsentModal = false;
    consentMessage = '';

    mayCreateOrCancelTestRun: boolean = false;
    faTv = faTv;

    constructor(private OAuth2AuthService: AuthService, private webmateOptions: WebmateOptions, public userService: UserService, private featureService: FeatureService,
                private router: Router, private toolService: ModalToolContainerService, private translate: TranslateService,
                private workbenchOpenService: WorkbenchOpenService) {
        translate.addLangs(['en']);
        translate.setDefaultLang('en');

        this.router.events.subscribe(
            (event: any) => {
                if (event instanceof NavigationEnd) {
                    let url = this.router.url.split('/');
                    for (let i = 0; i < url.length; i++) {
                        if (isUUID(url[i])) {
                            url[i] = 'id';
                        }
                        if (url[i].includes('?')) {
                            let s = url[i].split('?');
                            url[i] = s[0];

                        }
                    }
                    this.viewId = url.join('-').substr(1);
                }
            }
        );
        this.demoInstance = webmateOptions.demoenv;
    }

    ngOnInit() {
        // For some reason the route params are empty at this point, so we extract the data from the route itself
        let path = window.location.pathname;
        let projectsPrefix = '/projects/';
        if (path.startsWith(projectsPrefix) && path.length > projectsPrefix.length) {
            path = path.replace(projectsPrefix, '');
            const projectId = path.substring(0, 36 /* length of UUID*/);
            if (isUUID(projectId)) {
                this.currentProject = projectId;
                this.userService.hasPermissionInProject(this.currentProject, CreateOrCancelTestRunPermission).pipe(take(1)).subscribe(hasPermission => {
                    this.mayCreateOrCancelTestRun = hasPermission;
                });
            }
        }

        this.OAuth2AuthService.observeAuthStatus().pipe(mergeMap(next => {
            this.isAuthenticated = next;
            if (!this.isAuthenticated) {
                this.username = '';
                this.userId = undefined;
                this.showAdmin = false;
                // This might not be the right place for this code. However, the current separation of concerns inside the the AuthService
                // forbids a back channel from the Storage Strategy to the AuthService, therefore this works as a temporary fix
                // Normally navigating within a mergeMap would be a code smell, but due to the location.reload(),
                // any weird codeflow wouldn't matter anyway
                this.OAuth2AuthService.logout({preserveReturnUrl: true});
            }

            if (this.OAuth2AuthService.isInImpersonationMode()) {
                this.adminWarning = 'IMPERSONATION MODE ACTIVE';
            } else {
                this.adminWarning = undefined;
            }

            return this.userService.getCurrentUserData();
        }), mergeMap(next => {
            this.currentUser = next;
            this.username = next.name || '';
            this.userId = next.id;
            this.showAdmin = next.administrativePermissions.length > 0;
            if (next.isWebmateAdmin) {
                this.isWebmateAdmin = next.isWebmateAdmin;
            } else {
                this.isWebmateAdmin = false;
            }

            if ((window as any).Marker) {
                (window as any).Marker.setReporter({
                    email: this.currentUser!.email,
                    fullName: this.currentUser!.name,
                });
            }

            return this.featureService.getFeature('webmate.tracking-consent');
        }), mergeMap(f => {
            if (isDefined(f) && f.isActive) {
                this.displayConsentModal = this.currentUser!.publicProperties['trackingConsent'] === undefined;

                if (typeof f['infoText'] === 'string') {
                    this.consentMessage = f['infoText'].trim();
                }
            }
            return this.featureService.getFeature('webmate.frontend.internal-support-links');
        })).subscribe(feature => {
            if (feature?.isActive) {
                this.internalSupportFeature = feature;
            }
        });

        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                let projectsUrlStart = '/projects/';
                try {
                    if (event?.urlAfterRedirects.startsWith(projectsUrlStart)) {
                        let project = event.urlAfterRedirects.substring(projectsUrlStart.length, projectsUrlStart.length + 36 /* length of UUID */);
                        if (!isUUID(project)) {
                            this.currentProject = undefined;
                        } else if (project !== this.currentProject) {
                            this.currentProject = project;
                            this.userService.hasPermissionInProject(this.currentProject, CreateOrCancelTestRunPermission).pipe(take(1)).subscribe(hasPermission => {
                                this.mayCreateOrCancelTestRun = hasPermission;
                            });
                        }
                    } else {
                        this.currentProject = undefined;
                    }
                } catch (e) {
                    this.currentProject = undefined;
                }
            }
        });

        // Show the consent modal on demand
        this.toolService.openConsentModal$.subscribe(open => {
            this.displayConsentModal = true;
        });

        this.featureService.getFeature(MarketplaceFeatureId).pipe(take(1)).subscribe(feature => {
            this.marketplaceAvailable = feature?.isActive === true;
        });
    }

    get shouldShowRunTestDialog(): boolean {
        return !!this.currentProject && isUUID(this.currentProject) && this.mayCreateOrCancelTestRun;
    }

    get shouldShowOpenWorkbench(): boolean {
        return !!this.currentProject && isUUID(this.currentProject);
    }

    openRunTestDialog() {
        this.toolService.openTool(SelectTestComponent, new SelectTestToolConfig(this.currentProject!));
    }

    openWorkbench() {
        this.workbenchOpenService.openWorkbench({projectId: this.currentProject!}).pipe(take(1)).subscribe();
    }

    openMarketplace() {
        if (this.marketplaceAvailable) {
            this.toolService.openTool(MarketplaceComponent, new MarketplaceToolConfig(this.currentProject));
        }
    }

    saveConsentStatus(value: boolean) {
        this.userService.changeUserProperties(this.currentUser!.name, {...this.currentUser!.publicProperties, trackingConsent: value}, this.currentUser!.avatarId).pipe(take(1)).subscribe();
        this.displayConsentModal = false;
    }

    reload() {
        window.location.reload();
    }
}
